import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  getProfilesByApplication,
  getApplicationByProfile
} from '../logic';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ACTIONS } from '../../../../shared/constants';
import { getUser } from '../../../../shared/auth-service';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const ManagementPositionOptionDetail = ({
  data,
  handleClose,
  handleSave,
  employeePositionId,
  employeePosition,
  profiles,
  applications
}) => {
  const [selectedEmployeePositionId, setSelectedEmployeePosition] = useState(employeePositionId);
  const [selectedCodes, setSelectedCodes] = useState(data || []);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedCodesSend, setSelectedCodesSend] = useState({
    profiles: [],
    employeePositionId: selectedEmployeePositionId,
  });
  const [selectedCode, setSelectedCode] = useState(null);
  const [perfilId, setPerfilId] = useState(data.perfilId);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const userData = getUser();
  const companyId = userData.countryCode.split('-')[0];


  const handleAddEmployeePositions = (e, profileOverride) => {
    e.stopPropagation();

    const profile = profileOverride || selectedCode;
    if (!profile || !selectedApplicationId) return;

    const application = applications.find(a => a.id === selectedApplicationId);
    if (!application) return;

    // Cada par perfil+aplicación es único
  const alreadyExists = selectedCodes.some(
    el => el.profileId === profile.id && el.applicationId === application.id
  );

  if (alreadyExists) return;

  const updatedProfiles = [...selectedCodes, {
    id: `${profile.id}-${application.id}`,
    profileId: profile.id,
    description: profile.description,
    applicationId: application.id,
    applicationName: application.name,
    applicationIds: [application.id]
  }];

  setSelectedCodes(updatedProfiles);
  setIsDirty(true);
  setExpandedGroups(prev => new Set([...prev, application.name]));
  setSelectedCodesSend({
    profiles: updatedProfiles.map(({ profileId, description, applicationId }) => ({
      Id: profileId,
      Description: description,
      ApplicationIds: [applicationId]
    })),
    employeePositionId: selectedEmployeePositionId
  });
  };


  useEffect(() => {
    const normalizeProfilesWithApplications = async () => {
      if (data?.length > 0) {
        setLoadingProfiles(true);
        const normalized = [];

        for (const p of data) {

          const apps = await getApplicationByProfile(p.id, companyId);
          if (!apps || apps.length === 0) continue;
          for (const app of apps) {
            normalized.push({
              id: `${p.id}-${app.id}`,
              profileId: p.id,
              description: p.description,
              applicationId: app.id,
              applicationName: app.name,
              applicationIds: [app.id]
            });
          }
        }
        setSelectedCodes(normalized);
        setSelectedCodesSend({
          profiles: normalized.map(({ profileId, description, applicationId }) => ({
            Id: profileId,
            Description: description,
            ApplicationIds: [applicationId]
          })),
          employeePositionId: selectedEmployeePositionId
        });
        setLoadingProfiles(false);
      }
    };
    normalizeProfilesWithApplications();
  }, [applications, data]);


  useEffect(() => {
    const fetchProfiles = async () => {
      if (selectedApplicationId) {
        const filtered = await getProfilesByApplication(selectedApplicationId, companyId);
        setFilteredProfiles(filtered);
        setPerfilId('');
        setSelectedCode(null);
      } else {
        setFilteredProfiles([]);
      }
    };
    fetchProfiles();
  }, [selectedApplicationId]);


  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
          Perfiles por <span style={{ color: '#00b0ef' }}>Cargo</span>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Datos del Cargo
          </Typography>
          <TextField
            label="Cargo"
            fullWidth
            size="small"
            defaultValue={employeePosition}
            InputProps={{ readOnly: true }}
            variant="outlined"
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
          Asignación de Perfiles
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense">
              <Autocomplete
                id="autocomplete-application"
                options={applications}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={applications.find(a => a.id === selectedApplicationId) || null}
                onChange={(event, newValue) => {
                  setSelectedApplicationId(newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Aplicación" variant="outlined" size="small" />
                )}
                noOptionsText="Sin resultados"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense">
              <Autocomplete
                id="autocomplete-profile"
                options={selectedApplicationId ? filteredProfiles : []}
                getOptionLabel={(option) => option?.description || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCode}
                onChange={(event, newValue) => {
                  setSelectedCode(newValue || null);
                  setPerfilId(newValue ? newValue.id : '');
                  if (newValue) handleAddEmployeePositions({ stopPropagation: () => {} }, newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Perfil" variant="outlined" size="small" disabled={!selectedApplicationId} />
                )}
                noOptionsText="Seleccione una aplicación primero"
              />
            </FormControl>
          </Grid>
        </Grid>

        <Box mt={2}>
          {loadingProfiles ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>Cargando perfiles...</Typography>
            </Box>
          ) : selectedCodes.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
              <Typography variant="body2">Sin perfiles asignados</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
              {Object.entries(
                selectedCodes.reduce((groups, item) => {
                  const appName = item.applicationName || 'Sin aplicación';
                  if (!groups[appName]) groups[appName] = [];
                  groups[appName].push(item);
                  return groups;
                }, {})
              ).map(([appName, profs]) => (
                <Accordion key={appName} expanded={expandedGroups.has(appName)} disableGutters elevation={0}
                  onChange={(_, isExpanded) => setExpandedGroups(prev => { const next = new Set(prev); isExpanded ? next.add(appName) : next.delete(appName); return next; })}
                  sx={{ border: '1px solid #e0e4ed', borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: '#f0f4ff', borderRadius: '8px', minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          {appName}
                        </Typography>
                        <Chip label={profs.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1a3072', color: '#fff' }} />
                      </Box>
                      <Chip
                        icon={<DeleteSweepIcon sx={{ fontSize: '14px !important' }} />}
                        label="Quitar todo"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const ids = new Set(profs.map(p => p.id));
                          const updated = selectedCodes.filter(el => !ids.has(el.id));
                          setSelectedCodes(updated);
                          setIsDirty(true);
                          setSelectedCodesSend({
                            profiles: updated.map(({ profileId, description, applicationId }) => ({
                              Id: profileId, Description: description, ApplicationIds: [applicationId]
                            })),
                            employeePositionId: selectedEmployeePositionId
                          });
                        }}
                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#e53935', border: '1px solid #fecaca', '& .MuiChip-icon': { color: '#e53935' }, '&:hover': { bgcolor: '#fee2e2' } }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profs.map(p => (
                        <Chip
                          key={p.id}
                          label={p.description}
                          size="small"
                          onDelete={() => {
                            const updated = selectedCodes.filter(el => el.id !== p.id);
                            setSelectedCodes(updated);
                            setIsDirty(true);
                            setSelectedCodesSend({
                              profiles: updated.map(({ profileId, description, applicationId }) => ({
                                Id: profileId,
                                Description: description,
                                ApplicationIds: [applicationId]
                              })),
                              employeePositionId: selectedEmployeePositionId
                            });
                          }}
                          sx={{ bgcolor: '#e8f0fe', color: '#1a3072', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ m: 1 }}>
        <Box>
          <Button color="primary" variant="contained" onClick={() => handleSave(selectedCodesSend)} endIcon={<SaveIcon />}>
            {ACTIONS.SAVE}
          </Button>
        </Box>
        <Box sx={{ m: 1, position: 'relative' }}>
          <Button
            endIcon={<CancelIcon />}
            variant="outlined"
            onClick={() => isDirty ? setShowCancelAlert(true) : handleClose(false)}
            autoFocus
          >
            {ACTIONS.CANCEL}
          </Button>
        </Box>
      </DialogActions>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>
            ¿Descartar cambios?
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}
          >
            Seguir editando
          </Button>
          <Button
            variant="contained"
            onClick={() => { setShowCancelAlert(false); handleClose(false); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ManagementPositionOptionDetail;
